import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowRight, FiChevronDown } from "react-icons/fi";
import { BiBook, BiGroup, BiBuilding, BiTime } from "react-icons/bi";
import { settingsAPI } from "../../services/api";

const stats = [
  { icon: BiGroup, value: "1312", label: "Students" },
  { icon: BiBook, value: "43+", label: "Teachers" },
  { icon: BiBuilding, value: "30+", label: "Classrooms" },
  { icon: BiTime, value: "25+", label: "Years" },
];

const HeroSection = () => {
  const [particles, setParticles] = useState([]);
  const [heroMedia, setHeroMedia] = useState(null);

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        const media = res.data?.data?.settings?.heroMedia;
        if (media?.mediaType && media.mediaType !== "none" && media.url) {
          setHeroMedia(media);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const p = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      width: Math.random() * 60 + 10,
      top: Math.random() * 100,
      left: Math.random() * 85,   // cap at 85% so particles never bleed past the right edge
      delay: Math.random() * 4,
      duration: Math.random() * 4 + 3,
    }));
    setParticles(p);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden hero-gradient">
      {/* Hero background media */}
      {heroMedia?.mediaType === "video" && (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-0"
          src={heroMedia.url}
        />
      )}
      {heroMedia?.mediaType === "image" && (
        <img
          src={heroMedia.url}
          alt="School"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
      )}
      {/* Dark overlay so text stays readable over media */}
      {heroMedia && <div className="absolute inset-0 bg-black/55 z-0" />}
      {/* Animated Particles */}
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle absolute opacity-20"
          style={{
            width: p.width,
            height: p.width,
            top: `${p.top}%`,
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}

      {/* Decorative floating shapes — hidden on xs, shown sm+ */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="hidden sm:block absolute top-20 right-10 w-32 sm:w-40 h-32 sm:h-40 border-2 border-secondary-500/20 rounded-full"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        className="hidden sm:block absolute bottom-40 right-24 w-40 sm:w-60 h-40 sm:h-60 border border-secondary-500/10 rounded-full"
      />
      <motion.div
        animate={{ y: [-15, 15, -15] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="hidden md:block absolute top-1/3 right-1/4 w-12 h-12 bg-secondary-500/20 rounded-lg rotate-45"
      />
      <motion.div
        animate={{ y: [10, -10, 10] }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="hidden md:block absolute top-2/3 left-1/4 w-8 h-8 bg-white/10 rounded-full"
      />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "linear-gradient(#60a5fa 1px, transparent 1px), linear-gradient(90deg, #60a5fa 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-24 sm:pb-32 flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-secondary-500/20 border border-secondary-400/40 text-secondary-300 text-xs sm:text-sm font-medium mb-4 sm:mb-6"
        >
          <span className="w-2 h-2 rounded-full bg-secondary-400 animate-pulse shrink-0" />
          Admissions Open for 2025-2026
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-3xl sm:text-5xl lg:text-7xl font-serif font-bold text-white leading-tight mb-3 sm:mb-4"
        >
          Maria
          <span className="block" style={{ color: "#7dd3fc" }}>
            Education Foundation
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-base sm:text-xl lg:text-2xl text-white font-light mb-2 sm:mb-3 max-w-2xl"
        >
          Inspiring Excellence in Education
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-white/90 text-sm sm:text-base lg:text-lg mb-7 sm:mb-10 max-w-xl px-2 sm:px-0"
        >
          Nurturing young minds with quality education, values, and skills to
          excel in a dynamic world
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col xs:flex-row sm:flex-row gap-3 sm:gap-4 mb-10 sm:mb-16 w-full max-w-xs sm:max-w-none sm:w-auto"
        >
          <Link
            to="/admissions/apply"
            className="btn-primary flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            Apply Now
            <FiArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>
          <Link
            to="/academics"
            className="btn-outline flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            Explore Programs
          </Link>
        </motion.div>

        {/* Stats overlay */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 w-full max-w-3xl"
        >
          {stats.map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.2 + idx * 0.1 }}
              className="glass rounded-2xl p-3 sm:p-4 text-center"
            >
              <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-secondary-400 mx-auto mb-1.5 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white font-serif">
                {stat.value}
              </div>
              <div className="text-white/85 text-xs sm:text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 cursor-pointer"
      >
        <FiChevronDown className="w-6 h-6" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
